import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ADMIN, USER_ROLE_PARTNER } from 'common/userRoles';

import { getSunroomUtilityPortalUrl } from 'common/partnerHelpers';

import { SR_API_ACTION_METABASE_CHART_GET_BY_PARTNER_ID } from 'api/actionTypes';

import PartnerActions from 'client/actions/Partner';
import MetabaseChartActions from 'client/actions/MetabaseChart';

import { SCREEN_ID_PARTNER_REPORTS } from 'client/constants/screenIds';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import allowedRoles from 'client/helpers/allowedRoles';

import SrPartnerPage from 'client/components/SrPartnerPage';
import SrMetabaseCharts from 'client/components/SrMetabaseCharts/SrMetabaseCharts';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ partner }) => {
  if (!partner) {
    return {};
  }
  return {
    metabaseCharts: ['metabaseCharts', 'byPartnerId', partner.id],
  };
})
@withApiState({
  getMetabaseChartsApiState: {
    action: SR_API_ACTION_METABASE_CHART_GET_BY_PARTNER_ID,
  },
})
@withScreenId(SCREEN_ID_PARTNER_REPORTS)
@withScrollToTop
class PartnerReportsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Reports | Sunroom Rentals',
  });

  static propTypes = {
    partner: PropTypes.object,
    metabaseCharts: PropTypes.array,
    getMetabaseChartsApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return PartnerActions.loadPartnerBySlug(inject, {
      slug: match.params.partnerSlug,
    }).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      if (partner) {
        return MetabaseChartActions.getByPartnerId(inject, {
          partnerId: partner.id,
        });
      }
    });
  }

  render() {
    const { partner, metabaseCharts, getMetabaseChartsApiState, dispatch } = this.props;

    if (!partner || getMetabaseChartsApiState.inProgress) {
      return <div className="loading-gif" />;
    }

    if (Array.isArray(metabaseCharts) && metabaseCharts.length === 0) {
      return <Redirect to="/partner" />;
    }

    const utilitiesEnabled = partner.experienceConfiguration?.utilitiesEnabled || false;

    const navbarLinks = lodashCompact([
      {
        label: 'Home',
        value: `/partner/${partner.slug}`,
      },
      {
        label: 'Reports',
        value: `/partner/${partner.slug}/reports`,
      },
      utilitiesEnabled && {
        label: 'Utilities',
        value: getSunroomUtilityPortalUrl(partner),
      },
    ]);

    return (
      <SrPartnerPage navbarLinks={navbarLinks}>
        <SrBox padding="40px 0 30px">
          <SrFlex columnGap="15px" alignItems="baseline">
            <SrText font="greycliff" weight="700" size="28">
              Reports
            </SrText>
            <SrText font="greycliff" weight="500" size="16" color="grey400">
              {'Sunroom creates custom reports for your company! Please reach out ' +
                'to ben@sunroomrentals.com if you want more.'}
            </SrText>
          </SrFlex>
        </SrBox>
        <SrMetabaseCharts charts={metabaseCharts} dispatch={dispatch} />
      </SrPartnerPage>
    );
  }
}

PartnerReportsPage.displayName = 'PartnerReportsPage';

export default PartnerReportsPage;
